import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { publicApi, ApiError } from "@/api";
import type { PublicReadResponse } from "@/types";
import { getSessionId, isStaffSession, newEngagementUid } from "@/lib/session";
import { defaultSelectedOptionalIds } from "@/lib/money";
import { initEngagementBeacon } from "../../../../packages/engagement-beacon/beacon";
import { coarseUaFamily, deviceType } from "../../../../packages/engagement-beacon/ua";
import { FullPageSpinner } from "@/components/ui";
import { QuoteRenderer } from "@/renderer";
import {
  AcceptedScreen,
  DeclinedScreen,
  WithdrawnScreen,
  ExpiredScreen,
  NotFoundScreen,
  ErrorScreen,
} from "./states";
import "./public-quote.css";

type Load =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; data: PublicReadResponse };

export function PublicQuotePage() {
  const { slug = "" } = useParams();
  const [load, setLoad] = useState<Load>({ status: "loading" });
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState<string | undefined>(undefined);

  // Optional line-item selection — in-memory only (no storage), local until
  // accept. Opt-in: starts empty (nothing ticked).
  const [selectedOptionalIds, setSelectedOptionalIds] = useState<number[]>([]);

  const toggleOptional = useCallback((id: number) => {
    setSelectedOptionalIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }, []);

  // Guards so we fire `track` exactly once after mount (not on every render,
  // and not before the first paint) — sidesteps false views from preview bots.
  const trackedFor = useRef<string | null>(null);

  // Initial read (pure, no mutation — bot-safe).
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    setLoad({ status: "loading" });
    setSelectedOptionalIds([]); // reset opt-ins for a fresh quote

    publicApi
      .readBySlug(slug, controller.signal)
      .then((data) => {
        if (cancelled) return;
        setLoad({ status: "ready", data });
        // Seed default-selected optionals once per slug load (spec §6); the
        // running total then already reflects default-on extras.
        if (data.state === "live") {
          setSelectedOptionalIds(defaultSelectedOptionalIds(data.line_items));
        }
      })
      .catch((err) => {
        if (cancelled || (err instanceof DOMException && err.name === "AbortError")) return;
        const message = err instanceof ApiError ? err.message : "Unexpected error.";
        setLoad({ status: "error", message });
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [slug]);

  // Fire tracking AFTER mount + after the read resolves (spec §8). Runs once
  // per slug. Failures are swallowed — tracking must never block the page.
  useEffect(() => {
    if (load.status !== "ready") return;
    if (trackedFor.current === slug) return;
    trackedFor.current = slug;

    publicApi
      .track(slug, {
        session_id: getSessionId(),
        is_staff: isStaffSession(),
        user_agent: navigator.userAgent,
      })
      .catch(() => {
        /* tracking is best-effort */
      });
  }, [load.status, slug]);

  // The engagement beacon (shared with the feasibility report web view): per-block dwell,
  // time on page, scroll depth, posted cumulatively to api 334. Structurally unable to break
  // the page — fire-and-forget session mint, swallowed errors, and the quote renders exactly
  // the same if none of this runs.
  //
  // One session per PAGE LOAD per slug (a fresh uid, like the report's Worker-minted session
  // — not the per-tab track id). The ref keeps the uid stable across StrictMode's dev-time
  // double-run so the idempotent session endpoint sees one uid, not two.
  const engagementFor = useRef<{ slug: string; uid: string } | null>(null);
  const liveState = load.status === "ready" ? load.data.state : "";

  useEffect(() => {
    // Only a LIVE render carries the block document the beacon samples; the state screens
    // (accepted/declined/expired) have nothing to measure. Accepting mid-session flips the
    // state and the cleanup below stops the sampler — the pagehide-grade flush in stop()'s
    // callers has already banked the read.
    if (liveState !== "live") return;

    if (engagementFor.current?.slug !== slug) {
      engagementFor.current = { slug, uid: newEngagementUid() };
      publicApi
        .engagementSession(slug, {
          session_uid: engagementFor.current.uid,
          referrer: document.referrer,
          device_type: deviceType(navigator.userAgent),
          ua_family: coarseUaFamily(navigator.userAgent),
          is_staff: isStaffSession(),
        })
        .catch(() => {
          /* engagement is best-effort */
        });
    }

    const beacon = initEngagementBeacon({
      url: publicApi.engagementEventsUrl(slug),
      sessionId: engagementFor.current.uid,
    });
    return () => beacon?.stop();
  }, [liveState, slug]);

  async function refresh() {
    try {
      const data = await publicApi.readBySlug(slug);
      setLoad({ status: "ready", data });
    } catch {
      /* keep current view if the re-read fails */
    }
  }

  async function handleAccept(name?: string) {
    // Only acceptable from a live quote; capture its display fields first.
    if (load.status !== "ready" || load.data.state !== "live") return;
    const live = load.data;

    setActionBusy(true);
    setActionError(undefined);
    try {
      const result = await publicApi.accept(slug, {
        name: name ?? "",
        session_id: getSessionId(),
        selected_optional_line_item_ids: selectedOptionalIds,
      });
      // Build the accepted view from the live data we already hold plus the
      // server's authoritative figures — no extra read needed, and it mirrors
      // exactly what GET by-slug would return for an accepted quote.
      setLoad({
        status: "ready",
        data: {
          state: "accepted",
          quote_number: live.quote_number,
          project_description: live.project_description,
          accepted_total: result.accepted_total,
          selected_optional_line_item_ids: result.selected_optional_line_item_ids,
          line_items: live.line_items,
          currency: live.currency,
          vat_treatment: live.vat_treatment,
        },
      });
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : "Could not record your acceptance.");
      await refresh(); // state may have changed server-side (race guard)
    } finally {
      setActionBusy(false);
    }
  }

  async function handleDecline(reason?: string) {
    setActionBusy(true);
    setActionError(undefined);
    try {
      const result = await publicApi.decline(slug, { reason, session_id: getSessionId() });
      setLoad({ status: "ready", data: result });
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : "Could not record your response.");
      await refresh();
    } finally {
      setActionBusy(false);
    }
  }

  if (load.status === "loading") return <FullPageSpinner label="Loading quote…" />;
  if (load.status === "error") return <ErrorScreen message={load.message} />;

  const data = load.data;

  // Switch on the state discriminator (spec §8). converted → accepted handled
  // server-side, so it never reaches the client as its own state.
  switch (data.state) {
    case "accepted":
      return (
        <AcceptedScreen
          quoteNumber={data.quote_number}
          acceptedTotal={data.accepted_total}
          currency={data.currency}
          vatTreatment={data.vat_treatment}
          lineItems={data.line_items}
          selectedOptionalIds={data.selected_optional_line_item_ids}
          shortDescription={data.project_description}
        />
      );
    case "declined":
      return <DeclinedScreen quoteNumber={data.quote_number} />;
    case "withdrawn":
      return <WithdrawnScreen quoteNumber={data.quote_number} />;
    case "expired":
      return <ExpiredScreen quoteNumber={data.quote_number} expiryDate={data.expiry_date} />;
    case "not_found":
      return <NotFoundScreen />;
    case "live":
      // The renderer supplies the full CD page chrome (sticky header + footer),
      // so the public page and the builder preview stay pixel-identical (§2.1).
      return (
        <QuoteRenderer
          mode="public"
          blocks={data.content?.blocks ?? []}
          lineItems={data.line_items}
          quote={data}
          selectedOptionalIds={selectedOptionalIds}
          onToggleOptional={toggleOptional}
          onAccept={handleAccept}
          onDecline={handleDecline}
          acceptBusy={actionBusy}
          acceptError={actionError}
        />
      );
    default:
      // Forward-compatible: unknown state → not found.
      return <NotFoundScreen />;
  }
}
